import React from 'react';
import { StandardTableActions } from '../../../components/ui';

import { Column } from '../../../components/DataTable/DataTable';
import { PCrmInvoiceType } from './useData';
import { FieldConfig } from '../../../components/generic/fieldRenderers';
import { FormLayout } from '../../../components/generic/FormLayoutRenderer';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../../config/appConfig';

export const moduleConfig = {
  module: {
    title: 'Invoice Types',
    description: 'Manage invoice types',
    permissionModuleKey: 'invoice_type',
    route: {
      list: '/p_crm_invoice_type',
      new: '/p_crm_invoice_type/new',
      detail: (id: number) => `/p_crm_invoice_type/${id}`,
      edit: (id: number) => `/p_crm_invoice_type/${id}/edit`,
    },
    api: {
      list: '/p-crm-invoice-type',
      detail: (id: number) => `/p-crm-invoice-type/${id}`,
    },
  },
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions,
  },
  search: {
    placeholder: 'Search invoice types...',
  },
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '🔍',
      fields: ['pit_descr', 'pit_abbrev'],
    },
  ],
  filters: {
    fields: [
      {
        key: 'pit_descr',
        label: 'Description',
        placeholder: 'Type to filter description...',
        type: 'text' as const,
        getValue: (item: PCrmInvoiceType) => item.pit_descr,
        options: [],
      },
      {
        key: 'pit_abbrev',
        label: 'Abbreviation',
        placeholder: 'Type to filter abbreviation...',
        type: 'text' as const,
        getValue: (item: PCrmInvoiceType) => item.pit_abbrev ?? '',
        options: [],
      },
    ],
    applyFilter: (items: PCrmInvoiceType[], filters: { pit_descr: string[] | string; pit_abbrev: string[] | string }) => {
      return items.filter((item) => {
        const descrFilter = filters.pit_descr;
        if (typeof descrFilter === 'string' && descrFilter.trim() !== '') {
          const value = (item.pit_descr || '').toLowerCase();
          if (!value.includes(descrFilter.toLowerCase())) return false;
        } else if (Array.isArray(descrFilter) && descrFilter.length > 0) {
          const value = (item.pit_descr || '').toLowerCase();
          const matches = descrFilter.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }
        const abbrevFilter = filters.pit_abbrev;
        if (typeof abbrevFilter === 'string' && abbrevFilter.trim() !== '') {
          const value = (item.pit_abbrev ?? '').toLowerCase();
          if (!value.includes(abbrevFilter.toLowerCase())) return false;
        } else if (Array.isArray(abbrevFilter) && abbrevFilter.length > 0) {
          const value = (item.pit_abbrev ?? '').toLowerCase();
          const matches = abbrevFilter.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }
        return true;
      });
    },
  },
  toolbar: {
    actions: [
      {
        key: 'add',
        icon: 'add',
        title: 'Add New Invoice Type',
        showTitle: false,
        variant: 'primary' as const,
        href: '/p_crm_invoice_type/new',
      },
    ],
  },
  table: {
    actionsPosition: 'left' as const,
    onRowClick: (item: PCrmInvoiceType) => {
      window.location.href = `/p_crm_invoice_type/${item.id}`;
    },
    keyExtractor: (item: PCrmInvoiceType) => item.id,
  },
  emptyStates: {
    noData: 'Get started by creating a new invoice type.',
    noSearchResults: 'No invoice types found matching your search criteria.',
    noFilterResults: 'No invoice types match the selected filters.',
  },
  delete: {
    title: 'Delete Invoice Type',
    message: 'Are you sure you want to delete this invoice type? This action cannot be undone.',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Failed to delete invoice type. Please try again.',
    handler: async (id: number) => {
      const { deleteInvoiceType } = await import('../../../services/mutations');
      await deleteInvoiceType(id);
    },
  },
  utils: {
    formatDate: (dateString?: string) => {
      if (!dateString) return '-';
      return formatDateGlobal(dateString);
    },
  },
  detail: {
    subtitle: 'Invoice Type Details',
    notFoundTitle: 'Invoice Type not found',
    notFoundMessage: 'The invoice type you\'re looking for doesn\'t exist.',
    notFoundBackText: 'Back to Invoice Types',
    defaultTitle: 'Unnamed Invoice Type',
    getTitle: (item: PCrmInvoiceType) => {
      return item.pit_descr || `Invoice Type #${item.id}`;
    },
  },
  form: {
    new: {
      title: 'New Invoice Type',
      subtitle: 'Add a new invoice type',
      submitText: 'Create Invoice Type',
    },
    edit: {
      title: 'Edit Invoice Type',
      subtitle: 'Update invoice type information',
      submitText: 'Update Invoice Type',
    },
    cancelText: 'Cancel',
    loadingText: 'Loading invoice type...',
    errorMessage: 'Failed to save invoice type. Please try again.',
    create: async (data: Partial<PCrmInvoiceType>) => {
      const { createInvoiceType } = await import('../../../services/mutations');
      return await createInvoiceType(data);
    },
    update: async (id: number, data: Partial<PCrmInvoiceType>) => {
      const { updateInvoiceType } = await import('../../../services/mutations');
      return await updateInvoiceType(id, data);
    },
  },
  fields: [
    {
      name: 'pit_descr',
      label: 'Description',
      type: 'text',
      required: true,
      placeholder: 'Enter invoice type description',
      validation: {
        required: true,
        minLength: 1,
        maxLength: 255,
      },
    },
    {
      name: 'pit_abbrev',
      label: 'Abbreviation',
      type: 'text',
      placeholder: 'e.g. INV, EST',
      validation: {
        maxLength: 32,
      },
    },
  ] as FieldConfig[],
  formLayout: {
    mode: 'default',
    sections: [
      {
        id: 'basic',
        title: 'Invoice Type Information',
        icon: '📝',
        fields: ['pit_descr', 'pit_abbrev'],
        columns: 1,
        collapsible: false,
      },
    ],
  } as FormLayout,
};

export const getColumns = (formatDate: (dateString?: string) => string): Column<PCrmInvoiceType>[] => [
  {
    key: 'id',
    header: 'ID',
    sortable: true,
    render: (value) => (
      <span className="text-sm text-gray-900">{value || '-'}</span>
    ),
  },
  {
    key: 'pit_descr',
    header: 'Description',
    sortable: true,
    render: (value) => (
      <span className="text-sm text-gray-900">{value || '-'}</span>
    ),
  },
  {
    key: 'pit_abbrev',
    header: 'Abbreviation',
    sortable: true,
    render: (value) => (
      <span className="text-sm text-gray-900">{value ?? '-'}</span>
    ),
  },
];

export const getDetailSections = (
  item: PCrmInvoiceType,
  formatDate: (dateString?: string) => string
) => {
  return [
    {
      title: 'Basic Information',
      icon: '📝',
      fields: [
        {
          label: 'Description',
          value: item.pit_descr || '-',
        },
        {
          label: 'Abbreviation',
          value: item.pit_abbrev ?? '-',
        },
      ],
    },
  ];
};

export const getStatusBadges = (item: PCrmInvoiceType) => {
  return null;
};

export const getInitialFormData = (): Partial<PCrmInvoiceType> => {
  return {
    pit_descr: '',
    pit_abbrev: '',
  };
};

export const mapItemToFormData = (item: PCrmInvoiceType): Partial<PCrmInvoiceType> => {
  return {
    pit_descr: item.pit_descr || '',
    pit_abbrev: item.pit_abbrev ?? '',
  };
};

export const getToolbarIcon = (iconName: string): React.ReactNode => {
  switch (iconName) {
    case 'add':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      );
    default:
      return null;
  }
};

export const getTableActions = (
  item: PCrmInvoiceType,
  onDelete: (id: number) => void,
  _colors?: unknown,
  _listContext?: unknown,
  options?: { canDelete?: boolean; canEdit?: boolean }
): React.ReactNode => {
  const canDelete = options?.canDelete !== false;
  const canEdit = options?.canEdit !== false;
  return (
    <StandardTableActions
      viewTo={moduleConfig.module.route.detail(item.id)}
      editTo={moduleConfig.module.route.edit(item.id)}
      onDelete={() => onDelete(item.id)}
      canEdit={canEdit}
      canDelete={canDelete}
    />
  );
};

